/**
 * スクロールで現れる仕掛け
 *
 * @description マークアップ側の data-reveal で出方を選ぶ。4種類ある。
 *
 *              wipe     … 写真。マスクの位置をずらして、指定した辺から拭うように現れる
 *              lines    … 文章。行ごとに切り抜いて、段差をつけて下から持ち上げる
 *              lettering… 英文見出し。下から上がりきってから、左→右へ塗りが流れ込む
 *              fade     … パンくず・アンカー。少し下から持ち上げながら濃くする
 *
 *              これとは別に data-reveal-depth を持つ枠は、中の .revealpar を
 *              スクロールに合わせて上下に流す（視差）。出方と違って出きっても止まらない。
 *              層のはみ出しに収まらない量を指定したときは、収まらないぶんを枠ごと
 *              動かして稼ぐ。写真の切り取りを変えずに振れ幅だけ伸ばせる。
 *              枠ごと動く量はまわりのアキを食うので、狭いSPで減らしたい枠は
 *              data-reveal-depth-sp に別の値を置く。
 *              枠と離れてほしくない要素は data-reveal-with にセレクタを書くと、
 *              枠と同じだけ動いて位置関係が保たれる（枠の兄弟から探す）。
 *
 *              進行はスクロール量に直結させない。「もう出てよいか」の目標値だけを
 *              スクロールから出して、そこへ毎フレーム寄せる。スクロールを止めても
 *              動きが続くので、指を止めるたびに絵も止まる鈍さが出ない。
 */

import { gsap } from 'gsap/dist/gsap.min.js';
import { CustomEase } from 'gsap/dist/CustomEase.min.js';

gsap.registerPlugin(CustomEase);

const EASE = CustomEase.create('revealEase', '0.08, 0.7, 0.58, 1');

/** vwの基準幅。持ち上げ量をデザインの見た目どおりに保つのに使う */
const DESIGN_WIDTH = 1728;

/** SPの判定。視差の量をPCと変えている枠がある */
const SP = '(max-width: 1024px)';

/** 現れきるまでに使う高さ。画面の高さに対する割合 */
const RANGE = 0.2;

/** 立ち上がりにかける秒数。写真は速く、文字はゆっくり */
const PHOTO_SEC = 0.6;
const TEXT_SEC = 1.2;

/** 着地してから最初のひとつが出るまでと、そのあとのずらし幅（秒） */
const ENTER_DELAY = 0.12;
const ENTER_STAG = 0.09;

/** 1行を持ち上げるのに使う進行の幅と、行どうしの段差の上限 */
const LINE_SPAN = 0.5;
const LINE_STEP_MAX = 0.14;

/** 英文見出しが線だけでいる時間と、塗りきるまでの秒数 */
const LETTER_HOLD = 0.3;
const LETTER_FILL_SEC = 1.4;

/** fade で下から持ち上げる量（デザイン座標のpx） */
const FADE_SHIFT = 18;

/**
 * 視差で層を枠の中で流すとき、はみ出しを使いきらずに残す幅（枠の高さに対する%）。
 * 縁ぴったりまで寄せると、丸めの向き次第で枠の角に地が1px出る
 */
const PAR_EDGE_GUARD = 0.4;

/** マスクの向きごとの設定。from の位置から to の位置へ寄せるとワイプになる */
const DIRS: Record<string, { axis: string; size: string; from: [number, number]; to: [number, number] }> = {
    left: { axis: 'to right', size: '200% 100%', from: [100, 0], to: [0, 0] },
    right: { axis: 'to left', size: '200% 100%', from: [0, 0], to: [100, 0] },
    top: { axis: 'to bottom', size: '100% 200%', from: [0, 100], to: [0, 0] },
    bottom: { axis: 'to top', size: '100% 200%', from: [0, 0], to: [0, 100] },
};

type Item = {
    el: HTMLElement;
    /** 目標値を測るとき、要素の高さぶん待つか。文字は箱が入りきってから上げる */
    waitOwn: boolean;
    /** 毎フレームの寄せ幅 */
    rate: number;
    /** 画面に出た瞬間から見えている要素か。スクロールを待たず秒で出す */
    hero: boolean;
    /** hero の中での順番。ずらして出す */
    order: number;
    cur: number;
    paint: (p: number) => void;
    /** 出きったあとも書き続けるか。見出しの塗りは自分の時計で遅れて来る */
    more?: () => boolean;
};

/** 視差の対象。枠が画面を横切るあいだ、層と枠を +振れ幅 から -振れ幅 へ流す */
type Par = {
    frame: HTMLElement;
    layer: HTMLElement;
    /** 層を枠の中で流す量。層自身の高さに対する% */
    inner: number;
    /** 枠ごと流す量。枠の高さに対する% */
    drift: number;
    /** 直前に枠へ書いた量（px）。枠の位置を測るとき、これを引いて素の位置に戻す */
    applied: number;
    /** 枠と同じだけ動かす連れ。写真と説明文のアキをスクロール中も変えないために使う */
    tow: HTMLElement[];
};

let items: Item[] = [];
let pars: Par[] = [];
let running = false;

/** 画面幅の行き来を見張る口。組み直す対象を持ち回る */
const spMq = window.matchMedia(SP);
let parFrames: HTMLElement[] = [];

/** 画面が出てからの基準時刻。hero の発火に使う */
let born = 0;

/**
 * 0..1 に収める
 *
 * @param v 値
 * @returns 収めた値
 */
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * 秒で持った立ち上がりを、毎フレームの寄せ幅に直す
 *
 * @description (1 - rate) を毎フレーム掛けた結果が、その秒数で残り5%になる値
 * @param sec 立ち上がりの秒数
 * @returns 0..1 の寄せ幅
 */
const rateOf = (sec: number): number => 1 - Math.pow(0.05, 1 / Math.max(1, sec * 60));

/**
 * 写真のワイプを組み立てる
 *
 * @param el 対象
 * @returns 進行を受け取って見た目を書き込む関数
 */
const buildWipe = (el: HTMLElement): ((p: number) => void) => {
    const dir = DIRS[el.dataset.revealDir ?? 'left'] ?? DIRS.left;
    const img = `linear-gradient(${dir.axis}, #000 0 50%, transparent 50% 100%)`;

    el.style.maskImage = img;
    el.style.setProperty('-webkit-mask-image', img);
    el.style.maskSize = dir.size;
    el.style.setProperty('-webkit-mask-size', dir.size);
    el.style.maskRepeat = 'no-repeat';
    el.style.setProperty('-webkit-mask-repeat', 'no-repeat');

    return (p) => {
        const e = EASE(p);
        const x = dir.from[0] + (dir.to[0] - dir.from[0]) * e;
        const y = dir.from[1] + (dir.to[1] - dir.from[1]) * e;
        const pos = `${x.toFixed(2)}% ${y.toFixed(2)}%`;

        el.style.maskPosition = pos;
        el.style.setProperty('-webkit-mask-position', pos);
    };
};

/**
 * 行ごとの持ち上げを組み立てる
 *
 * @param lines 1行ぶんの中身
 * @param down 上から出すか（縦組みは列なので上から）
 * @param span 持ち上げきるまでに使う進行の幅
 * @returns 進行を受け取って見た目を書き込む関数
 */
const buildLift = (lines: HTMLElement[], down: boolean, span: number = LINE_SPAN): ((p: number) => void) => {
    const step = Math.min(LINE_STEP_MAX, span / Math.max(1, lines.length - 1));
    const sign = down ? -1 : 1;

    return (p) => {
        lines.forEach((ln, i) => {
            const lp = EASE(clamp01((p - i * step) / span));

            ln.style.transform = `translate3d(0, ${(sign * (1 - lp) * 100).toFixed(2)}%, 0)`;
        });
    };
};

/**
 * 英文見出しの塗りを流し込む窓を返す
 *
 * @param el 字形の箱
 * @returns 窓と字形の横幅。揃っていなければ null
 */
const wipesOf = (el: HTMLElement) => {
    const svg = el.querySelector<SVGSVGElement>('.Lettering__svg');
    const fill = el.querySelector<SVGRectElement>('[data-lettering-fillwipe]');
    const line = el.querySelector<SVGRectElement>('[data-lettering-linewipe]');

    return svg && fill && line ? { svg, fill, line, w: svg.viewBox.baseVal.width } : null;
};

/**
 * 英文見出しを組み立てる
 *
 * @description 塗りは進行の割合ではなく秒で持つ。毎フレーム目標へ寄せる進行そのものが
 *              「最初が速くて後が粘る」形をしているので、そこへ同じ向きの曲線を重ねると
 *              二段になって、秒数を伸ばしても初速だけが残る。字が動き出したところで
 *              時計を回し始めて、経過秒から素の進行を作り、曲線は一度だけ掛ける
 * @param el 対象
 * @returns 書き込む関数と、出きったあとも書き続けるかの判定。組み立てられなければ null
 */
const buildLettering = (el: HTMLElement): { paint: (p: number) => void; more: () => boolean } | null => {
    const w = wipesOf(el);

    if (!w) return null;

    const lift = buildLift([w.svg as unknown as HTMLElement], false);

    let start = -1;

    const paint = (p: number) => {
        lift(p);

        if (start < 0 && p > 0.02) start = performance.now();

        // 線だけでいる時間を置いてから塗りはじめる。塗りながら上がると字が二重に動いて見える
        const t = start < 0 ? 0 : (performance.now() - start) / 1000;
        const fw = w.w * EASE(clamp01((t - LETTER_HOLD) / LETTER_FILL_SEC));

        w.fill.setAttribute('width', fw.toFixed(2));
        // 塗りが来たところの輪郭を消す。1.5ユニット重ねて境目に隙間を作らない。
        // ただし端までは重ねない。定数のまま引くと塗りきっても輪郭が1.5だけ残る
        const overlap = Math.max(0, Math.min(1.5, w.w - fw));
        const x = Math.max(0, fw - overlap);

        w.line.setAttribute('x', x.toFixed(2));
        w.line.setAttribute('width', (w.w - x).toFixed(2));
    };

    // 字は先に上がりきるが、塗りは自分の時計で遅れて来る。進行が1になった時点で
    // 書き込みを止めると、塗りが途中で固まる
    const more = (): boolean => start < 0 || (performance.now() - start) / 1000 < LETTER_HOLD + LETTER_FILL_SEC;

    return { paint, more };
};

/**
 * 少し下から持ち上げながら濃くする出方を組み立てる
 *
 * @param el 対象
 * @returns 進行を受け取って見た目を書き込む関数
 */
const buildFade = (el: HTMLElement): ((p: number) => void) => (p) => {
    const e = EASE(p);
    const shift = FADE_SHIFT * (window.innerWidth / DESIGN_WIDTH);

    el.style.opacity = String(e);
    el.style.transform = `translate3d(0, ${((1 - e) * shift).toFixed(1)}px, 0)`;
};

/**
 * 出方に合わせて書き込む関数を組み立てる
 *
 * @param el 対象
 * @returns 書き込む関数と、出きったあとも書き続けるかの判定。組み立てられなければ null
 */
const buildOf = (el: HTMLElement): { paint: (p: number) => void; more?: () => boolean } | null => {
    const kind = el.dataset.reveal;

    if (kind === 'wipe') return { paint: buildWipe(el) };
    if (kind === 'lettering') return buildLettering(el);
    if (kind === 'fade') return { paint: buildFade(el) };

    if (kind === 'lines') {
        // 画面幅で見せ替える行（mq4pc / mq4sp）は両方DOMに居る。段差の幅も順番も
        // 行数から決まるので、いま出ていない行を数に入れると見えている行がずれる
        const lines = Array.from(el.querySelectorAll<HTMLElement>('[data-reveal-line]')).filter((ln) => ln.getClientRects().length > 0);

        return lines.length ? { paint: buildLift(lines, el.hasAttribute('data-reveal-down')) } : null;
    }

    return null;
};

/**
 * 1フレーム分。全要素の進行を進めて書き込む
 *
 * @returns {void}
 */
const step = (): void => {
    const h = window.innerHeight;
    const span = RANGE * h;
    const t = (performance.now() - born) / 1000;

    // 測りと書き込みは分ける。1要素ずつ交互にやると、書いた直後に測ることになって
    // フレームごとにレイアウトを取り直す羽目になる
    const wants = items.map((it) => {
        if (it.cur >= 1) return 1;

        const r = it.el.getBoundingClientRect();
        const scrollWant = clamp01((h - r.top - (it.waitOwn ? r.height : 0)) / span);

        // 着いた瞬間に見えているものはスクロールを待たずに出す。待つと
        // 遷移してきた直後の画面が空のままになる
        return it.hero ? (t > ENTER_DELAY + it.order * ENTER_STAG ? 1 : 0) : scrollWant;
    });

    items.forEach((it, i) => {
        if (it.cur >= 1 && !(it.more?.() ?? false)) return;

        // 戻ったときに引っ込むとフェードアウトに見えるので、目標値は下げない
        const target = wants[i] < it.cur ? it.cur : wants[i];

        it.cur += (target - it.cur) * it.rate;

        if (it.cur > 0.9999) it.cur = 1;

        it.paint(it.cur);
    });

    // 視差は出きったあとも続く。枠が画面を横切るあいだ、ずっと流しつづける
    pars.forEach((pa) => {
        const r = pa.frame.getBoundingClientRect();
        // 枠を動かしているぶんが測った位置に乗っているので、引いて素の位置に戻す。
        // 戻さないと自分の動きが次のフレームの目標値に混ざって、際限なく流れていく
        const q = clamp01((h - (r.top - pa.applied)) / (h + r.height));
        const k = 1 - 2 * q;

        pa.layer.style.transform = `translate3d(0, ${(pa.inner * k).toFixed(2)}%, 0)`;

        if (!pa.drift) return;

        pa.applied = (pa.drift / 100) * r.height * k;

        // 連れには枠と同じ量を渡す。層の流れぶんは写真が窓の中で滑る動きなので混ぜない
        const move = `translate3d(0, ${pa.applied.toFixed(2)}px, 0)`;

        pa.frame.style.transform = move;
        pa.tow.forEach((el) => {
            el.style.transform = move;
        });
    });
};

/**
 * 視差で書いた見た目を戻して、対象を空にする
 *
 * @returns {void}
 */
const clearPars = (): void => {
    pars.forEach((pa) => {
        pa.frame.style.transform = '';
        pa.layer.style.transform = '';
        pa.tow.forEach((el) => {
            el.style.transform = '';
        });
    });

    pars = [];
};

/**
 * 仕掛けをすべて破棄する
 *
 * @returns {void}
 */
const destroyReveal = (): void => {
    if (running) {
        gsap.ticker.remove(step);
        running = false;
    }

    spMq.removeEventListener('change', onSpChange);
    clearPars();

    items = [];
    parFrames = [];
};

/**
 * 動かさずに、出きった状態で置く
 *
 * @param el 対象
 * @returns {void}
 */
const settle = (el: HTMLElement): void => {
    const kind = el.dataset.reveal;

    if (kind === 'lettering') {
        const w = wipesOf(el);

        if (w) {
            w.fill.setAttribute('width', String(w.w));
            w.line.setAttribute('width', '0');
            w.svg.style.transform = '';
        }
    }

    el.style.opacity = '';
    el.style.transform = '';
    el.querySelectorAll<HTMLElement>('[data-reveal-line]').forEach((ln) => {
        ln.style.transform = '';
    });
};

/**
 * 視差の対象を測って組み直す
 *
 * @description 振れ幅の割り振りは枠と層の高さから決まるので、一度測って持っておく。
 *              vwで組んだ高さは幅が変わっても比が動かないが、SPで消える枠と
 *              data-reveal-depth-sp は画面幅の行き来で変わるので、そこだけ測り直す
 * @param frames 対象の枠
 * @returns {void}
 */
const buildPars = (frames: HTMLElement[]): void => {
    clearPars();

    const sp = window.matchMedia(SP).matches;

    frames.forEach((frame) => {
        const layer = frame.querySelector<HTMLElement>('.revealpar');
        const d = Number((sp && frame.dataset.revealDepthSp) || frame.dataset.revealDepth);

        if (!layer || !Number.isFinite(d) || d === 0) return;

        const frameH = frame.getBoundingClientRect().height;
        const layerH = layer.getBoundingClientRect().height;

        // 画面幅で消える枠（SPで隠す添えの写真）は測れない。流す相手も居ない
        if (!frameH || !layerH) return;

        // 層は枠より上下へはみ出させてあり、その余りの中でしか流せない。はみ出しを
        // 増やすと object-fit: cover で絵が寄って左右が欠けるので、余りに収まらない
        // ぶんは枠ごと動かすほうへ回す。切り抜き写真は余りが無いので全部が枠側になる
        const ratio = layerH / frameH;
        // 望む振れ幅と、はみ出しの余り。どちらも枠の高さに対する%でそろえる
        const want = d * ratio;
        const room = Math.max(0, ((ratio - 1) / 2) * 100);
        // 余りに収まるなら層だけで流す。枠を動かさないので余計な合成層も作らない
        const inner = Math.abs(want) <= room ? want : Math.sign(d) * Math.max(0, room - PAR_EDGE_GUARD);

        // 連れは枠の兄弟から探す。同じ札の中に枠が2つ（大小の写真）あるので、
        // どちらに付いていくかはマークアップ側で選ばせる
        const tow = frame.dataset.revealWith && frame.parentElement
            ? Array.from(frame.parentElement.querySelectorAll<HTMLElement>(frame.dataset.revealWith))
            : [];

        pars.push({ frame, layer, inner: inner / ratio, drift: want - inner, applied: 0, tow });
    });
};

/**
 * 画面幅がPCとSPを行き来したときに視差を組み直す
 *
 * @returns {void}
 */
const onSpChange = (): void => buildPars(parFrames);

/**
 * 仕掛けを始める
 *
 * @returns {void}
 */
const initReveal = (): void => {
    destroyReveal();

    const all = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    // 呼ばれたときに出すものは、隠れているあいだに出きってしまわないよう数に入れない
    const targets = all.filter((el) => !el.hasAttribute('data-reveal-manual'));
    const frames = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal-depth]'));

    if (!targets.length && !frames.length) return;

    // 現れ方は装飾なので、モーションを抑える設定では出しきった状態で置く。
    // 視差も動かさない（層は枠の中央に収まったままになる）
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        all.forEach(settle);
        return;
    }

    parFrames = frames;
    buildPars(frames);
    spMq.addEventListener('change', onSpChange);

    const h = window.innerHeight;
    // 順番は写真と文字で別に数える。混ぜると、文字が多いページで写真だけ
    // いつまでも出てこない
    const orders = { wipe: 0, text: 0 };

    targets.forEach((el) => {
        const kind = el.dataset.reveal;
        const built = buildOf(el);

        if (!built) return;

        const { paint, more } = built;

        const r = el.getBoundingClientRect();
        const hero = r.top < h && r.bottom > 0;
        const lane = kind === 'wipe' ? 'wipe' : 'text';

        items.push({
            el,
            waitOwn: kind !== 'wipe',
            rate: rateOf(kind === 'wipe' ? PHOTO_SEC : TEXT_SEC),
            hero,
            order: hero ? orders[lane]++ : 0,
            cur: 0,
            paint,
            more,
        });
    });

    if (!items.length && !pars.length) return;

    born = performance.now();
    // 最初の1フレームを待たずに隠す。待つと出きった状態が一瞬見える
    items.forEach((it) => it.paint(0));

    gsap.ticker.add(step);
    running = true;
};

/**
 * 1つの要素を、スクロールを待たずに頭から出し直す
 *
 * @description 画面の中で入れ替わる見出しに使う。すでに出きった要素でも隠した状態へ戻してから出す
 * @param el data-reveal を持つ要素。呼ぶ時点で表示されている（display:none でない）こと
 * @returns {void}
 */
const playReveal = (el: HTMLElement): void => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        settle(el);
        return;
    }

    const built = buildOf(el);

    if (!built) return;

    items = items.filter((it) => it.el !== el);
    // hero は基準時刻からの経過で目標が1になる。とうに過ぎているので、着いたフレームから上がり出す
    items.push({ el, waitOwn: false, rate: rateOf(TEXT_SEC), hero: true, order: 0, cur: 0, paint: built.paint, more: built.more });
    built.paint(0);

    if (!running) {
        gsap.ticker.add(step);
        running = true;
    }
};

export { initReveal, destroyReveal, playReveal };

document.addEventListener('DOMContentLoaded', initReveal);
